import React, { useEffect, useState, useCallback } from "react";
import { X, ArrowLeft, Users } from "lucide-react";
import { toast } from "react-toastify";
import { useCurrentDomain } from "../context/DomainContext";
import { getCompaniesByFeature, getUsersByFeature } from "../services/masterDataService";
import { THEME } from "../theme/themeColors";

const FeatureUsageModal = ({ featureName, onClose }) => {
  const { domain } = useCurrentDomain();

  const [view, setView] = useState("companies");
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [companiesTotal, setCompaniesTotal] = useState(0);

  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);

  const totalPagesCompanies = Math.max(1, Math.ceil(companiesTotal / pageSize));
  const totalPagesUsers = Math.max(1, Math.ceil(usersTotal / pageSize));

  const loadCompanies = useCallback(
    async (page = 1, search = "") => {
      if (!domain || !featureName) return;
      setLoading(true);
      try {
        const data = await getCompaniesByFeature(domain, featureName, search, page, pageSize);
        setCompanies(data?.items || []);
        setCompaniesTotal(data?.totalCount || 0);
        setPageNumber(page);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load companies");
        setCompanies([]);
        setCompaniesTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [domain, featureName, pageSize]
  );

  const loadUsers = useCallback(
    async (companyId, page = 1, search = "") => {
      if (!domain || !featureName || !companyId) return;
      setLoading(true);
      try {
        const data = await getUsersByFeature(domain, featureName, companyId, search, page, pageSize);
        setUsers(data?.items || []);
        setUsersTotal(data?.totalCount || 0);
        setPageNumber(page);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load users");
        setUsers([]);
        setUsersTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [domain, featureName, pageSize]
  );

  useEffect(() => {
    setView("companies");
    setSelectedCompany(null);
    setSearchTerm("");
    setPageNumber(1);
    loadCompanies(1, "");
  }, [featureName, loadCompanies]);

  const handleSearch = () => {
    if (view === "companies") {
      loadCompanies(1, searchTerm);
    } else {
      loadUsers(selectedCompany.companyId, 1, searchTerm);
    }
  };

  const handleCompanyClick = (company) => {
    setSelectedCompany(company);
    setView("users");
    setSearchTerm("");
    setPageNumber(1);
    loadUsers(company.companyId, 1, "");
  };

  const handleBack = () => {
    setView("companies");
    setSelectedCompany(null);
    setSearchTerm("");
    setPageNumber(1);
    loadCompanies(1, "");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(1100px, 96vw)",
          maxHeight: "90vh",
          height: "90vh",
          background: "#fff",
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            background: THEME.primary,
            color: THEME.onPrimary,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {view === "users" && (
              <button
                type="button"
                onClick={handleBack}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: `1px solid ${THEME.onPrimary}`,
                  color: THEME.onPrimary,
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "12px",
                }}
              >
                <ArrowLeft size={14} />
                Back
              </button>
            )}
            <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 600 }}>
              {view === "companies"
                ? `${featureName} — Companies`
                : `${featureName} — ${selectedCompany?.companyName || ""} (${selectedCompany?.companyId || ""})`}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: THEME.onPrimary,
              cursor: "pointer",
              padding: "2px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div
          style={{
            padding: "10px 16px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              view === "companies"
                ? "Search company name or ID..."
                : "Search user name or ID..."
            }
            className="form-control form-control-sm"
            style={{ fontSize: "12px", maxWidth: "320px" }}
          />
          <button
            type="button"
            onClick={handleSearch}
            className="btn btn-sm"
            style={{
              background: THEME.primary,
              color: THEME.onPrimary,
              border: "none",
              fontSize: "12px",
              fontWeight: 600,
              padding: "4px 12px",
            }}
          >
            Search
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "0" }}>
          {loading ? (
            <div className="text-center py-5">
              <div
                className="spinner-border spinner-border-sm"
                style={{ color: THEME.primary }}
                role="status"
              />
            </div>
          ) : view === "companies" ? (
            <table
              className="table table-sm table-hover mb-0"
              style={{ fontSize: "13px" }}
            >
              <thead>
                <tr style={{ background: THEME.primary, color: THEME.onPrimary }}>
                  <th style={{ padding: "10px 12px", fontWeight: 600 }}>Company Name</th>
                  <th style={{ padding: "10px 12px", fontWeight: 600 }}>Company ID</th>
                  <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "center" }}>
                    User Count
                  </th>
                </tr>
              </thead>
              <tbody>
                {companies.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-4">
                      No companies found
                    </td>
                  </tr>
                ) : (
                  companies.map((c) => (
                    <tr key={c.companyId}>
                      <td style={{ padding: "8px 12px" }}>
                        <button
                          type="button"
                          onClick={() => handleCompanyClick(c)}
                          style={{
                            border: "none",
                            background: "none",
                            padding: 0,
                            color: THEME.primaryHover,
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontSize: "13px",
                          }}
                        >
                          {c.companyName}
                        </button>
                      </td>
                      <td style={{ padding: "8px 12px" }}>{c.companyId}</td>
                      <td style={{ padding: "8px 12px", textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => handleCompanyClick(c)}
                          style={{
                            border: "none",
                            background: "none",
                            padding: 0,
                            color: THEME.primaryHover,
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontSize: "13px",
                          }}
                        >
                          {c.userCount}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table
              className="table table-sm table-hover mb-0"
              style={{ fontSize: "12px" }}
            >
              <thead>
                <tr style={{ background: THEME.primary, color: THEME.onPrimary }}>
                  <th style={{ padding: "8px 10px" }}>User Name</th>
                  <th style={{ padding: "8px 10px" }}>User ID</th>
                  <th style={{ padding: "8px 10px", textAlign: "center" }}>Status</th>
                  <th style={{ padding: "8px 10px" }}>Expiry Date</th>
                  <th style={{ padding: "8px 10px" }}>Created Date</th>
                  <th style={{ padding: "8px 10px" }}>Modified By</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id || u.userId}>
                      <td style={{ padding: "7px 10px" }}>{u.userName}</td>
                      <td style={{ padding: "7px 10px" }}>{u.userId}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: "12px",
                            background: u.isEnabled ? THEME.alpha(0.12) : "#f8d7da",
                            color: u.isEnabled ? THEME.primaryHover : "#dc3545",
                          }}
                        >
                          {u.isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </td>
                      <td style={{ padding: "7px 10px" }}>
                        {u.expiryDate
                          ? new Date(u.expiryDate).toLocaleDateString("en-US")
                          : "—"}
                      </td>
                      <td style={{ padding: "7px 10px" }}>
                        {u.createdDate
                          ? new Date(u.createdDate).toLocaleDateString("en-US")
                          : "—"}
                      </td>
                      <td style={{ padding: "7px 10px" }}>{u.modifiedBy || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {(view === "companies" ? companiesTotal : usersTotal) > 0 && (
          <div
            style={{
              padding: "10px 16px",
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "12px",
              color: "#6b7280",
            }}
          >
            <span>
              {view === "companies"
                ? `${(pageNumber - 1) * pageSize + 1}–${Math.min(
                    pageNumber * pageSize,
                    companiesTotal
                  )} of ${companiesTotal}`
                : `${(pageNumber - 1) * pageSize + 1}–${Math.min(
                    pageNumber * pageSize,
                    usersTotal
                  )} of ${usersTotal}`}
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: "12px" }}
                disabled={pageNumber <= 1 || loading}
                onClick={() => {
                  if (view === "companies") {
                    loadCompanies(pageNumber - 1, searchTerm);
                  } else {
                    loadUsers(selectedCompany.companyId, pageNumber - 1, searchTerm);
                  }
                }}
              >
                Prev
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: "12px" }}
                disabled={
                  pageNumber >=
                    (view === "companies" ? totalPagesCompanies : totalPagesUsers) ||
                  loading
                }
                onClick={() => {
                  if (view === "companies") {
                    loadCompanies(pageNumber + 1, searchTerm);
                  } else {
                    loadUsers(selectedCompany.companyId, pageNumber + 1, searchTerm);
                  }
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureUsageModal;